import type { ReactNode } from 'react';

import { useCallback } from 'react';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import rightArrowIcon from '../../images/icons-library/carets/caret_right_blue-icon.svg';

interface BreadcrumbProps {
  key: string;
  link?: string;
  label?: ReactNode;
  disabled?: boolean;
  additionalClasses?: string;
  children?: ReactNode;
  onClick?: () => void;
}

interface BreadcrumbsProps {
  /**
   * Breadcrumbs list with all needed attributes
   */
  breadcrumbs?: BreadcrumbProps[];
  /**
   * Breadcrumbs additional CSS classes
   */
  additionalClasses?: string;
  /**
   * Single Breadcrumb additional CSS classes
   */
  additionalBreadcrumbClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const Breadcrumbs = ({
  breadcrumbs = EMPTY_ARRAY,
  additionalClasses = '',
  additionalBreadcrumbClasses = '',
  testId = '',
}: BreadcrumbsProps) => {
  const renderBreadcrumb = useCallback((breadcrumb: BreadcrumbProps) => {
    const { children, disabled, additionalClasses, label, link, onClick } =
      breadcrumb;
    const labelTitle =
      typeof label === 'string' && label?.length > 30 ? label : '';

    if (children) return children;

    if (link && !disabled) {
      return (
        <Link
          className={twMerge(additionalClasses || '')}
          to={link}
          title={labelTitle}
          onClick={onClick}
        >
          {label}
        </Link>
      );
    }

    return (
      <p
        className={twMerge(
          disabled && 'text-slate-400 dark:text-slate-200',
          additionalClasses || '',
        )}
        title={labelTitle}
      >
        {label}
      </p>
    );
  }, []);

  return (
    <div
      className={twMerge(
        'flex p-3 text-slate-700 whitespace-nowrap border-slate-800',
        'max-w-[calc(100vw-94px)] sm:max-w-[calc(100vw-114px)] md:max-w-[calc(100vw-430px)]',
        'lg:max-w-[calc(100vw-430px)]',
        additionalClasses,
      )}
      {...getTestProps(testId, 'wrapper')}
    >
      {breadcrumbs?.map((breadcrumb) => (
        <div
          key={breadcrumb.key}
          className={twMerge(
            'pr-5 last:pr-0! flex items-center bg-contain bg-no-repeat bg-right last:bg-none! text-xs',
            'dark:text-slate-600! dark:last:text-slate-200! truncate min-w-8',
            additionalBreadcrumbClasses,
          )}
          style={{
            backgroundImage: `url("${rightArrowIcon}")`,
            backgroundSize: '1.1rem',
          }}
          {...getTestProps(testId, breadcrumb.key)}
        >
          {renderBreadcrumb(breadcrumb)}
        </div>
      ))}
    </div>
  );
};

export default Breadcrumbs;
